import { Icon } from '../icons';
import React, { useMemo } from 'react';

import AssetUploader from './AssetUploader';
import { ProjectAsset } from '../../types/editor';

interface AddVideoModalProps {
  projectId: number;
  /** Full asset list — filtered to image + video inside. */
  assets: ProjectAsset[];
  /** Timeline time the user clicked. Shown in the header. */
  atTime: number;
  /** Pick an existing asset → wires straight to ADD_CLIP. */
  onPick: (asset: ProjectAsset) => void;
  /** Newly uploaded asset → caller adds to state + adds a clip. */
  onUploaded: (asset: ProjectAsset) => void;
  onClose: () => void;
}

function fmtSec(t: number | null | undefined): string {
  if (!t || !isFinite(t)) return '—';
  const m = Math.floor(t / 60);
  const s = Math.floor(t - m * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtTimeMSC(t: number): string {
  if (!isFinite(t) || t < 0) return '0:00:00';
  const totalCs = Math.round(t * 100);
  const m = Math.floor(totalCs / 6000);
  const s = Math.floor((totalCs % 6000) / 100);
  const cs = totalCs % 100;
  return `${m}:${String(s).padStart(2, '0')}:${String(cs).padStart(2, '0')}`;
}

export default function AddVideoModal({
  projectId, assets, atTime, onPick, onUploaded, onClose,
}: AddVideoModalProps) {

  /* The video track accepts both image and video clips. Audio assets
   * belong to the audio track and are filtered out here. */
  const videoAssets = useMemo(
    () => assets.filter((a) => a.kind === 'image' || a.kind === 'video'),
    [assets],
  );

  return (
    <div
      className="modal modal--centered"
      role="dialog"
      aria-modal="true"
      aria-label="Add video clip"
    >
      <div className="modal__panel">
        <header className="modal__head">
          <h3>Add to video track <span className="muted">@ {fmtTimeMSC(atTime)}</span></h3>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
          ><Icon name="close" /></button>
        </header>

        <div className="modal__body">
          <section className="add-audio-section">
            <div className="section-label">From project assets</div>
            {videoAssets.length === 0 ? (
              <p className="muted">
                No image or video assets in this project yet — upload one below.
              </p>
            ) : (
              <ul className="add-media-tiles">
                {videoAssets.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      className={`add-media-tile kind-${a.kind}`}
                      onClick={() => onPick(a)}
                    >
                      <span className="add-media-thumb">
                        {a.kind === 'image' && a.file_url && (
                          <img alt={a.original_filename} src={a.file_url} />
                        )}
                        {a.kind === 'video' && a.file_url && (
                          <video src={a.file_url} muted preload="metadata" playsInline />
                        )}
                        <span className="add-media-kind">{a.kind}</span>
                      </span>
                      <span className="add-media-meta">
                        <span className="add-media-name" title={a.original_filename}>
                          {a.original_filename || `Asset #${a.id}`}
                        </span>
                        <span className="muted add-media-dur">{fmtSec(a.duration)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="add-audio-section">
            <div className="section-label">Or upload a new file</div>
            <AssetUploader
              projectId={projectId}
              onUploaded={(asset) => {
                /* Only route image / video uploads back through
                 * onUploaded — audio uploads still register on the
                 * project (AssetUploader saved them) but don't drop
                 * onto the VIDEO track. The audio track has its own
                 * picker for that. */
                if (asset.kind === 'image' || asset.kind === 'video') {
                  onUploaded(asset);
                }
              }}
            />
            <small className="muted">
              Images and videos only — audio uploads go to the project library but won't drop onto the video track.
            </small>
          </section>
        </div>

        <footer className="modal__foot">
          <button type="button" className="ghost" onClick={onClose}>Cancel</button>
        </footer>
      </div>
    </div>
  );
}
